import React, { useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

/**
 * Component hiển thị và quản lý PDF viewer
 * 
 * @param {Object} props
 * @param {string} props.pdfUrl - URL của file PDF (đã convert qua CDN)
 * @param {string} props.fileName - Tên file để hiển thị và download
 */
export default function PDFViewer({ pdfUrl, fileName = 'document.pdf' }) {
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [downloading, setDownloading] = useState(false);

    // Khởi tạo plugins
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const zoomPluginInstance = zoomPlugin();

    // Destructure các components từ plugins
    const { GoToFirstPage, GoToLastPage, GoToNextPage, GoToPreviousPage, CurrentPageInput } = 
        pageNavigationPluginInstance;
    const { ZoomIn, ZoomOut, Zoom } = zoomPluginInstance;

    /**
     * Callback khi document load xong
     * Lưu tổng số trang và set trang hiện tại = 1
     */
    const handleDocumentLoad = (e) => {
        setTotalPages(e.doc.numPages);
        setCurrentPage(1);
    };

    /**
     * Callback khi người dùng chuyển trang
     * Index bắt đầu từ 0, nên +1 để hiển thị
     */
    const handlePageChange = (e) => {
        setCurrentPage(e.currentPage + 1);
    };

    /**
     * Xử lý download file PDF với tên file đã decode
     * Dùng Fetch API để tải blob, sau đó trigger download
     */
    const handleDownload = async () => {
        try {
            setDownloading(true);
            
            // Fetch file từ CDN
            const response = await fetch(pdfUrl);
            if (!response.ok) throw new Error('Network response was not ok');
            
            const blob = await response.blob();
            
            // Tạo URL tạm thời từ blob
            const url = window.URL.createObjectURL(blob);
            
            // Tạo link ẩn và trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            
            // Cleanup: xóa link và revoke URL
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download error:', err);
            alert('Không thể tải file. Vui lòng thử lại!');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="pdf-viewer-container">
            {/* Custom Toolbar */}
            <div className="card mb-2">
                <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        {/* Navigation Controls - Điều hướng trang */}
                        <div className="btn-group" role="group" aria-label="Page navigation">
                            <GoToFirstPage>
                                {(props) => (
                                    <button 
                                        className="btn btn-sm btn-outline-secondary" 
                                        onClick={props.onClick}
                                        disabled={currentPage === 1}
                                        title="Trang đầu tiên"
                                        aria-label="Trang đầu tiên"
                                    >
                                        <i className="fas fa-step-backward"></i>
                                    </button>
                                )}
                            </GoToFirstPage>
                            
                            <GoToPreviousPage>
                                {(props) => (
                                    <button 
                                        className="btn btn-sm btn-outline-secondary" 
                                        onClick={props.onClick}
                                        disabled={currentPage === 1}
                                        title="Trang trước"
                                        aria-label="Trang trước"
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                )}
                            </GoToPreviousPage>

                            <GoToNextPage>
                                {(props) => (
                                    <button 
                                        className="btn btn-sm btn-outline-secondary" 
                                        onClick={props.onClick}
                                        disabled={currentPage === totalPages}
                                        title="Trang sau"
                                        aria-label="Trang sau"
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                )}
                            </GoToNextPage>

                            <GoToLastPage>
                                {(props) => (
                                    <button 
                                        className="btn btn-sm btn-outline-secondary" 
                                        onClick={props.onClick}
                                        disabled={currentPage === totalPages}
                                        title="Trang cuối"
                                        aria-label="Trang cuối"
                                    >
                                        <i className="fas fa-step-forward"></i>
                                    </button>
                                )}
                            </GoToLastPage>
                        </div>

                        {/* Page Info - Hiển thị trang hiện tại / tổng số trang */}
                        <div className="d-flex align-items-center">
                            <span className="me-2">Trang</span>
                            <CurrentPageInput>
                                {(props) => (
                                    <input
                                        type="number"
                                        className="form-control form-control-sm text-center"
                                        style={{ width: '70px' }}
                                        value={currentPage}
                                        min={1}
                                        max={totalPages}
                                        onChange={(e) => {
                                            const pageNumber = parseInt(e.target.value, 10);
                                            if (pageNumber > 0 && pageNumber <= totalPages) {
                                                props.onKeyDown({
                                                    key: 'Enter',
                                                    target: { value: pageNumber }
                                                });
                                            }
                                        }}
                                        aria-label="Số trang hiện tại"
                                    />
                                )}
                            </CurrentPageInput>
                            <span className="ms-2">/ {totalPages}</span>
                        </div>

                        {/* Zoom Controls - Phóng to/thu nhỏ */}
                        <div className="btn-group" role="group" aria-label="Zoom controls">
                            <ZoomOut>
                                {(props) => (
                                    <button 
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={props.onClick}
                                        title="Thu nhỏ"
                                        aria-label="Thu nhỏ"
                                    >
                                        <i className="fas fa-search-minus"></i>
                                    </button>
                                )}
                            </ZoomOut>

                            <Zoom>
                                {(props) => (
                                    <button className="btn btn-sm btn-outline-secondary" disabled>
                                        {Math.round(props.scale * 100)}%
                                    </button>
                                )}
                            </Zoom>

                            <ZoomIn>
                                {(props) => (
                                    <button 
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={props.onClick}
                                        title="Phóng to"
                                        aria-label="Phóng to"
                                    >
                                        <i className="fas fa-search-plus"></i>
                                    </button>
                                )}
                            </ZoomIn>
                        </div>

                        {/* Download Button - Nút tải xuống */}
                        <button 
                            className="btn btn-sm btn-primary"
                            onClick={handleDownload}
                            disabled={downloading}
                            title={`Tải xuống: ${fileName}`}
                            aria-label="Tải xuống file PDF"
                        >
                            {downloading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Đang tải...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-download me-1"></i>
                                    Tải xuống
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* File Info - Hiển thị tên file */}
            <div className="alert alert-info py-2 mb-2">
                <small>
                    <i className="fas fa-file-pdf me-2"></i>
                    <strong>File:</strong> {fileName}
                </small>
            </div>

            {/* PDF Viewer - Khung hiển thị PDF */}
            <div className='card card-body border-0 p-0' style={{ height: '700px' }}>
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <Viewer 
                        fileUrl={pdfUrl}
                        plugins={[
                            pageNavigationPluginInstance,
                            zoomPluginInstance
                        ]}
                        onDocumentLoad={handleDocumentLoad}
                        onPageChange={handlePageChange}
                    />
                </Worker>
            </div>
        </div>
    );
}